import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getContracts, createContract, updateContract, signContract, terminateContract, extractErrorMessage } from '../services/api';
import { FaPlus, FaEdit, FaFileSignature, FaBan, FaEye, FaFileContract } from 'react-icons/fa';
import UserSelect from '../components/UserSelect';
import '../styles/Contracts.css';
import { useToast } from '../components/Toast';

function Contracts({ user }) {
  const toast = useToast();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [editingContract, setEditingContract] = useState(null);
  const [viewingContract, setViewingContract] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    dominant_id: '',
    submissive_id: '',
    terms: '',
    hard_limits: '',
    soft_limits: '',
    safe_word: '',
    duration: '',
    review_date: '',
    notes: ''
  });

  useEffect(() => {
    fetchContracts();
  }, [filter]);

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { status: filter } : {};
      const data = await getContracts(params);
      setContracts(data.contracts || data);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      toast.error(extractErrorMessage(error, 'Verträge konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        ...formData,
        terms: formData.terms ? formData.terms.split('\n').filter(t => t.trim()) : [],
        hard_limits: formData.hard_limits ? formData.hard_limits.split(',').map(l => l.trim()).filter(l => l) : [],
        soft_limits: formData.soft_limits ? formData.soft_limits.split(',').map(l => l.trim()).filter(l => l) : [],
        review_date: formData.review_date ? new Date(formData.review_date).toISOString() : null
      };
      if (editingContract) {
        await updateContract(editingContract.id, payload);
      } else {
        await createContract(payload);
      }
      resetForm();
      setShowModal(false);
      toast.success(editingContract ? 'Vertrag gespeichert' : 'Vertrag erstellt');
      fetchContracts();
    } catch (error) {
      console.error('Error saving contract:', error);
      setFormError(extractErrorMessage(error, 'Fehler beim Speichern des Vertrags'));
    } finally {
      setSaving(false);
    }
  };

  const handleSign = async (id) => {
    if (!window.confirm('Vertrag wirklich unterschreiben?')) return;
    try {
      await signContract(id);
      toast.success('Vertrag unterschrieben');
      fetchContracts();
    } catch (error) {
      console.error('Error signing contract:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Unterschreiben des Vertrags'));
    }
  };

  const handleTerminate = async (id) => {
    if (!window.confirm('Vertrag wirklich kündigen?')) return;
    try {
      await terminateContract(id);
      toast.success('Vertrag gekündigt');
      fetchContracts();
    } catch (error) {
      console.error('Error terminating contract:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Kündigen des Vertrags'));
    }
  };

  const handleEdit = (contract) => {
    setEditingContract(contract);
    setFormData({
      title: contract.title || '',
      dominant_id: contract.dominant_id || '',
      submissive_id: contract.submissive_id || '',
      terms: Array.isArray(contract.terms) ? contract.terms.join('\n') : (contract.terms || ''),
      hard_limits: Array.isArray(contract.hard_limits) ? contract.hard_limits.join(', ') : (contract.hard_limits || ''),
      soft_limits: Array.isArray(contract.soft_limits) ? contract.soft_limits.join(', ') : (contract.soft_limits || ''),
      safe_word: contract.safe_word || '',
      duration: contract.duration || '',
      review_date: contract.review_date ? contract.review_date.slice(0, 10) : '',
      notes: contract.notes || ''
    });
    setShowModal(true);
  };

  const handleView = (contract) => {
    setViewingContract(contract);
    setShowDetailModal(true);
  };

  const resetForm = () => {
    setEditingContract(null);
    setFormData({ title: '', dominant_id: '', submissive_id: '', terms: '', hard_limits: '', soft_limits: '', safe_word: '', duration: '', review_date: '', notes: '' });
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setFormError(null);
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { class: 'draft', label: 'Entwurf' },
      active: { class: 'active', label: 'Aktiv' },
      terminated: { class: 'terminated', label: 'Gekündigt' },
      expired: { class: 'expired', label: 'Abgelaufen' }
    };
    return badges[status] || badges.draft;
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1><FaFileContract /> Verträge</h1>
          <button className="add-btn" onClick={openCreateModal}>
            <FaPlus /> Neuer Vertrag
          </button>
        </div>

        <div className="filter-tabs">
          <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>Alle</button>
          <button className={filter === 'draft' ? 'active' : ''} onClick={() => setFilter('draft')}>Entwürfe</button>
          <button className={filter === 'active' ? 'active' : ''} onClick={() => setFilter('active')}>Aktiv</button>
          <button className={filter === 'terminated' ? 'active' : ''} onClick={() => setFilter('terminated')}>Gekündigt</button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="contracts-list">
            {contracts.length > 0 ? (
              contracts.map(contract => {
                const statusBadge = getStatusBadge(contract.status);
                return (
                  <div key={contract.id} className={`contract-card ${contract.status}`}>
                    <div className="contract-header">
                      <h3 className="contract-title">{contract.title || 'Unbenannter Vertrag'}</h3>
                      <span className={`status-badge ${statusBadge.class}`}>{statusBadge.label}</span>
                    </div>
                    <div className="contract-parties">
                      <span className="party dominant">Dom: {contract.dominant_id || '?'}</span>
                      <span className="party-divider">&mdash;</span>
                      <span className="party submissive">Sub: {contract.submissive_id || '?'}</span>
                    </div>
                    {contract.safe_word && (
                      <div className="contract-safeword">
                        <span className="safeword-label">Safe Word:</span> {contract.safe_word}
                      </div>
                    )}
                    <div className="contract-meta">
                      {contract.duration && <span>Dauer: {contract.duration}</span>}
                      {contract.review_date && (
                        <span>Review: {new Date(contract.review_date).toLocaleDateString('de-DE')}</span>
                      )}
                      {contract.created_at && (
                        <span>Erstellt: {new Date(contract.created_at).toLocaleDateString('de-DE')}</span>
                      )}
                    </div>
                    <div className="contract-actions">
                      <button className="action-btn view" onClick={() => handleView(contract)} title="Details anzeigen">
                        <FaEye /> Details
                      </button>
                      {contract.status === 'draft' && (
                        <>
                          <button className="action-btn sign" onClick={() => handleSign(contract.id)} title="Unterschreiben">
                            <FaFileSignature /> Unterschreiben
                          </button>
                          <button className="action-btn edit" onClick={() => handleEdit(contract)} title="Bearbeiten">
                            <FaEdit />
                          </button>
                        </>
                      )}
                      {contract.status === 'active' && (
                        <button className="action-btn terminate" onClick={() => handleTerminate(contract.id)} title="Kündigen">
                          <FaBan /> Kündigen
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="no-contracts">Keine Verträge gefunden</div>
            )}
          </div>
        )}

        {/* Detail Modal */}
        {showDetailModal && viewingContract && (
          <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
            <div className="modal detail-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>{viewingContract.title || 'Vertragsdetails'}</h2>
              <div className="detail-section">
                <h4>Status</h4>
                <span className={`status-badge ${viewingContract.status}`}>
                  {getStatusBadge(viewingContract.status).label}
                </span>
              </div>
              <div className="detail-section">
                <h4>Parteien</h4>
                <p>Dominant: {viewingContract.dominant_id || '-'}</p>
                <p>Submissive: {viewingContract.submissive_id || '-'}</p>
              </div>
              {viewingContract.terms && (
                <div className="detail-section">
                  <h4>Bedingungen</h4>
                  <ul className="terms-list">
                    {(Array.isArray(viewingContract.terms) ? viewingContract.terms : [viewingContract.terms]).map((term, i) => (
                      <li key={i}>{term}</li>
                    ))}
                  </ul>
                </div>
              )}
              {viewingContract.hard_limits && (
                <div className="detail-section">
                  <h4>Hard Limits</h4>
                  <div className="limits-tags">
                    {(Array.isArray(viewingContract.hard_limits) ? viewingContract.hard_limits : [viewingContract.hard_limits]).map((limit, i) => (
                      <span key={i} className="limit-tag hard">{limit}</span>
                    ))}
                  </div>
                </div>
              )}
              {viewingContract.soft_limits && (
                <div className="detail-section">
                  <h4>Soft Limits</h4>
                  <div className="limits-tags">
                    {(Array.isArray(viewingContract.soft_limits) ? viewingContract.soft_limits : [viewingContract.soft_limits]).map((limit, i) => (
                      <span key={i} className="limit-tag soft">{limit}</span>
                    ))}
                  </div>
                </div>
              )}
              {viewingContract.safe_word && (
                <div className="detail-section">
                  <h4>Safe Word</h4>
                  <p className="safeword-display">{viewingContract.safe_word}</p>
                </div>
              )}
              {viewingContract.notes && (
                <div className="detail-section">
                  <h4>Notizen</h4>
                  <p>{viewingContract.notes}</p>
                </div>
              )}
              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowDetailModal(false)}>Schließen</button>
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>{editingContract ? 'Vertrag bearbeiten' : 'Neuer Vertrag'}</h2>
              {formError && <div className="shared-form-error-banner">{formError}</div>}
              <form onSubmit={handleCreate}>
                <div className="form-group">
                  <label>Titel *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={e => setFormData({...formData, title: e.target.value})}
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Dominant *</label>
                    <UserSelect
                      value={formData.dominant_id}
                      onChange={id => setFormData({...formData, dominant_id: id})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Submissive *</label>
                    <UserSelect
                      value={formData.submissive_id}
                      onChange={id => setFormData({...formData, submissive_id: id})}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Bedingungen (eine pro Zeile)</label>
                  <textarea
                    value={formData.terms}
                    onChange={e => setFormData({...formData, terms: e.target.value})}
                    placeholder="Bedingung 1&#10;Bedingung 2&#10;Bedingung 3"
                  />
                </div>
                <div className="form-group">
                  <label>Hard Limits (kommagetrennt)</label>
                  <input
                    type="text"
                    value={formData.hard_limits}
                    onChange={e => setFormData({...formData, hard_limits: e.target.value})}
                    placeholder="Limit 1, Limit 2, Limit 3"
                  />
                </div>
                <div className="form-group">
                  <label>Soft Limits (kommagetrennt)</label>
                  <input
                    type="text"
                    value={formData.soft_limits}
                    onChange={e => setFormData({...formData, soft_limits: e.target.value})}
                    placeholder="Limit 1, Limit 2, Limit 3"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Safe Word</label>
                    <input
                      type="text"
                      value={formData.safe_word}
                      onChange={e => setFormData({...formData, safe_word: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Dauer</label>
                    <input
                      type="text"
                      value={formData.duration}
                      onChange={e => setFormData({...formData, duration: e.target.value})}
                      placeholder="z.B. 3 Monate"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Review-Datum</label>
                  <input
                    type="date"
                    value={formData.review_date}
                    onChange={e => setFormData({...formData, review_date: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Notizen</label>
                  <textarea
                    value={formData.notes}
                    onChange={e => setFormData({...formData, notes: e.target.value})}
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeModal} disabled={saving}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={saving}>
                    {saving ? 'Speichert\u2026' : (editingContract ? 'Speichern' : 'Erstellen')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Contracts;
